/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { withProtectedRoute } from "../../../utils/HOC/withProtectedRoute";
import { PrimaryButton } from "../../../components";
import { useNavigate } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { ChallengesTable } from "./ChallengesTable";
import {
  IChallenge,
  usePartnerChallenges,
} from "../../../store/usePartnerChallenges";

const PAGE_SIZE = 10;

// A challenge is no longer running once it is switched off or its end date has
// passed - the app treats both the same way.
const isEnded = (challenge: any) => {
  const status = challenge?.status;
  if (status && status !== "ACTIVE") return true;
  const endsAt = challenge?.endsAt;
  return endsAt != null && new Date(endsAt).getTime() < Date.now();
};

interface ChallengesCardProps {
  title: string;
  subtitle?: string;
  emptyText: string;
  challenges: IChallenge[];
  ended?: boolean;
  className?: string;
}

/** One list of challenges in its own card, paged on the client: the whole list
 *  is already loaded, so paging only keeps a long list readable. */
const ChallengesCard = ({
  title,
  subtitle,
  emptyText,
  challenges,
  ended = false,
  className = "",
}: ChallengesCardProps) => {
  const [page, setPage] = useState<number>(1);
  const totalPages = Math.max(1, Math.ceil(challenges.length / PAGE_SIZE));
  // Switching a challenge on or off, or deleting one, moves it out of this
  // card, which can leave it a page short. The saved page is lowered too, or
  // the card would jump back to it once the list grows again.
  const currentPage = Math.min(page, totalPages);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pageChallenges = challenges.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div
      className={`w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none ${className}`}
    >
      <h2
        className={`font-satoshi text-lg font-bold text-dark ${
          subtitle ? "mb-1" : "mb-4"
        }`}
      >
        {title} ({challenges.length})
      </h2>
      {subtitle ? (
        <p className="text-sm text-gray-600 mb-4">{subtitle}</p>
      ) : null}
      {!challenges.length ? (
        <p className="text-center font-satoshi text-base text-dark">
          {emptyText}
        </p>
      ) : (
        <ChallengesTable challenges={pageChallenges} ended={ended} />
      )}
      <div className="flex items-center justify-between mt-4">
        <p className="text-xs text-gray-500">
          Page {currentPage} of {totalPages}
        </p>
        <div className="flex gap-2">
          <PrimaryButton
            label="Previous"
            size="sm"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="!bg-white !border-gray-border disabled:opacity-40"
            textClassName="!text-dark"
          />
          <PrimaryButton
            label="Next"
            size="sm"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark disabled:opacity-40"
          />
        </div>
      </div>
    </div>
  );
};

function PartnerChallengesScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const {
    getAllPartnerChallenges,
    allPartnerChallenges,
    updatedPartnerChallenge,
    clearUpdatedPartnerChallenge,
  } = usePartnerChallenges();

  useEffect(() => {
    getAllPartnerChallenges();
  }, []);

  // The switches in the table update a challenge from here, and the edit
  // screen navigates straight back whenever it finds this flag set. Left
  // standing, the next click on Edit opened the form and bounced to this
  // list again. Clear it as soon as an update finishes on this screen.
  useEffect(() => {
    if (updatedPartnerChallenge) {
      clearUpdatedPartnerChallenge();
    }
  }, [updatedPartnerChallenge]);

  // Finished campaigns get their own card so a long tail of them does not
  // bury the live ones.
  const { activeChallenges, inactiveChallenges } = useMemo(() => {
    const all = allPartnerChallenges ?? [];
    return {
      activeChallenges: all.filter((challenge) => !isEnded(challenge)),
      inactiveChallenges: all.filter(isEnded),
    };
  }, [allPartnerChallenges]);

  /** Functions */
  const addChallenge = () => navigate("/add-partner-challenge");
  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Partner Challenges
        </h1>
        <PrimaryButton
          onClick={addChallenge}
          label="Add new"
          className="h-10"
        />
      </div>
      {!allPartnerChallenges?.length ? (
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          <p className="text-center font-satoshi text-base text-dark">
            There is no Partner Challenge created yet.
          </p>
        </div>
      ) : (
        <>
          <ChallengesCard
            title="Active challenges"
            emptyText="There is no active challenge right now."
            challenges={activeChallenges}
          />
          <ChallengesCard
            title="Inactive challenges"
            subtitle="Switched off, or past their end date."
            emptyText="There is no inactive challenge."
            challenges={inactiveChallenges}
            ended
            className="mt-6"
          />
        </>
      )}
    </div>
  );
}

export default withProtectedRoute(PartnerChallengesScreen);
